import { useCallback, useEffect, useState } from 'react';
import { Download, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface CommissionRule {
  id: string;
  role: string;
  ratePercent: number;
  minThreshold: number;
  effectiveFrom: string;
}

interface CommissionResult {
  userId: string;
  email: string;
  role: string;
  wonValue: number;
  ratePercent: number;
  commission: number;
}

const ROLES = ['SALES_REPRESENTATIVE', 'SALES_MANAGER', 'PRE_SALES'] as const;

export function CommissionsPage() {
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [results, setResults] = useState<CommissionResult[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [newRule, setNewRule] = useState({
    role: 'SALES_REPRESENTATIVE' as string,
    ratePercent: '',
    minThreshold: '0',
    effectiveFrom: '',
  });

  // Default period: current quarter
  const now = new Date();
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
  const [periodStart] = useState(quarterStart.toISOString().slice(0, 10));
  const [periodEnd] = useState(quarterEnd.toISOString().slice(0, 10));

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const loadRules = useCallback(async () => {
    try {
      const { data } = await apiClient.get<CommissionRule[]>('/commission-rules');
      setRules(data);
    } catch {
      setRules([]);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try {
      const { data } = await apiClient.get<CommissionResult[]>('/analytics/commissions', {
        params: { periodStart, periodEnd },
      });
      setResults(data);
    } catch {
      setResults([]);
    }
  }, [periodStart, periodEnd]);

  useEffect(() => {
    loadRules();
    loadReport();
  }, [loadRules, loadReport]);

  async function addRule() {
    if (!newRule.ratePercent || !newRule.effectiveFrom) return;
    try {
      await apiClient.post('/commission-rules', {
        role: newRule.role,
        ratePercent: parseFloat(newRule.ratePercent),
        minThreshold: parseFloat(newRule.minThreshold) || 0,
        effectiveFrom: newRule.effectiveFrom,
      });
      setNewRule({ role: 'SALES_REPRESENTATIVE', ratePercent: '', minThreshold: '0', effectiveFrom: '' });
      loadRules();
      loadReport();
    } catch {
      showToast('Failed to add rule');
    }
  }

  async function deleteRule(id: string) {
    try {
      await apiClient.delete(`/commission-rules/${id}`);
      loadRules();
      loadReport();
    } catch {
      showToast('Failed to delete rule');
    }
  }

  function exportCsv() {
    const header = 'email,role,won_value,rate_percent,commission\n';
    const rows = results
      .map(
        (r) =>
          `${r.email},${r.role},${r.wonValue},${r.ratePercent},${r.commission}`,
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commissions-${periodStart}-${periodEnd}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      <h1 className="text-2xl font-semibold">Commissions</h1>

      {/* Commission Rules */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Commission Rules
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2 text-right">Rate %</th>
                <th className="px-3 py-2 text-right">Min Threshold</th>
                <th className="px-3 py-2">Effective From</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td className="px-3 py-2">
                    <span className="rounded bg-blue-950/40 px-1.5 py-0.5 text-xs font-medium text-blue-300">
                      {rule.role.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{parseFloat(String(rule.ratePercent)).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right">
                    {parseFloat(String(rule.minThreshold)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2">{rule.effectiveFrom}</td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteRule(rule.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No commission rules defined.
                  </td>
                </tr>
              )}
              {/* Add rule row */}
              <tr className="bg-muted/30">
                <td className="px-3 py-2">
                  <select
                    value={newRule.role}
                    onChange={(e) => setNewRule({ ...newRule, role: e.target.value })}
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    value={newRule.ratePercent}
                    onChange={(e) => setNewRule({ ...newRule, ratePercent: e.target.value })}
                    placeholder="0.0"
                    className="h-8 w-20 text-right text-xs"
                    step="0.1"
                    min={0}
                    max={100}
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    value={newRule.minThreshold}
                    onChange={(e) => setNewRule({ ...newRule, minThreshold: e.target.value })}
                    placeholder="0"
                    className="h-8 w-28 text-right text-xs"
                    step="100"
                    min={0}
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="date"
                    value={newRule.effectiveFrom}
                    onChange={(e) => setNewRule({ ...newRule, effectiveFrom: e.target.value })}
                    className="h-8 text-xs"
                  />
                </td>
                <td className="px-3 py-2">
                  <Button
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={addRule}
                    disabled={!newRule.ratePercent || !newRule.effectiveFrom}
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Commission Report */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Commission Report
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Period: {periodStart} to {periodEnd}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={exportCsv}
            disabled={results.length === 0}
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Rep</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2 text-right">Won Value</th>
                <th className="px-3 py-2 text-right">Rate %</th>
                <th className="px-3 py-2 text-right">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {results.map((r) => (
                <tr key={r.userId}>
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-blue-950/40 px-1.5 py-0.5 text-xs font-medium text-blue-300">
                      {r.role.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {parseFloat(String(r.wonValue)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right">{parseFloat(String(r.ratePercent)).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {parseFloat(String(r.commission)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No commissions calculated for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {results.length > 0 && (
          <div className="mt-3 flex justify-end border-t pt-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Total Commissions: </span>
              <span className="font-semibold">
                {results
                  .reduce((sum, r) => sum + parseFloat(String(r.commission)), 0)
                  .toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
